import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize, forkJoin } from 'rxjs';
import { Company } from 'src/app/company/company.model';
import { CrmAccount, CrmClient } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { Location } from 'src/app/location/location.model';
import { LocationService } from 'src/app/location/location.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { City } from 'src/app/models/common/city.model';
import { Country } from 'src/app/models/common/country.model';
import { BaseMasterDataService } from 'src/app/services/base-master-data.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../../shared/party-quick-create/party-quick-create-dialog.component';
import { WarehousingMasterType, companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb, warehousingMasterTypeDescription, warehousingMasterTypeLabel, warehousingMasterTypeRoute } from '../warehousing-common';
import { WarehousingMasterData, WarehousingRegionMapping } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-master-data-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, CheckboxModule,
    FieldsetModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, PartyQuickCreateDialogComponent, ProgressBarModule, SelectModule, LazyLocationPickerDirective, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-master-data-form.component.html',
  styleUrl: './warehousing-master-data-form.component.css'
})
export class WarehousingMasterDataFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  constructor(
    @Optional() private dialogRef: DynamicDialogRef | null,
    @Optional() private dialogConfig: DynamicDialogConfig | null
  ) {}
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  isStandaloneContext = false;
  isDialogMode = false;
  isEmbeddedMode = false;
  isWarehouse = false;
  type: WarehousingMasterType = 'REGION';
  returnEvent = 'WAREHOUSING_WAREHOUSE_CREATED';
  title = 'Setup Value';
  setupDescription = 'Maintain this setup dataset on its dedicated page.';
  listRoute = '/setup/regions';
  locations: Location[] = [];
  clients: CrmClient[] = [];
  suppliers: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  countries: Country[] = [];
  private citiesByCountryId = new Map<number, City[]>();
  quickCreateVisible = false;
  warehouseOptions: WarehousingMasterData[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Setup Value', 'Setup', '/warehousing/setup/storage-rates');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  masterForm = this.fb.group({
    id: [null as number | null],
    type: ['REGION' as WarehousingMasterType, Validators.required],
    name: ['', Validators.required],
    code: [''],
    parentId: [null as number | null],
    crmClientId: [null as number | null],
    crmAccountId: [null as number | null],
    categoryId: [null as number | null],
    brandId: [null as number | null],
    locationId: [null as number | null],
    defaultWarehouseId: [null as number | null],
    supplierClientId: [null as number | null],
    supplierWarehouseName: [''],
    warehouseOwnershipType: ['COMPANY'],
    isActive: [true],
    regionMappings: this.fb.array([])
  });

  ngOnInit(): void {
    this.isDialogMode = !!this.dialogRef;
    const id = Number(this.dialogConfig?.data?.id ?? this.route.snapshot.paramMap.get('id')) || undefined;
    const requestedType = (this.dialogConfig?.data?.type ?? this.route.snapshot.queryParamMap.get('type')) as WarehousingMasterType | null;
    const routeType = (this.route.snapshot.data['masterType'] || this.route.snapshot.paramMap.get('type') || requestedType || 'REGION') as WarehousingMasterType;
    const parentCategoryId = Number(this.dialogConfig?.data?.parentId ?? this.route.snapshot.queryParamMap.get('parentId')) || null;
    const type = routeType;
    this.isEditMode = !!id;
    this.isStandaloneContext = this.route.snapshot.data['standaloneContext'] === true;
    this.isEmbeddedMode = this.route.snapshot.queryParamMap.get('embedded') === '1';
    this.returnEvent = this.route.snapshot.queryParamMap.get('returnEvent') || this.returnEvent;
    this.type = type;
    this.isWarehouse = type === 'WAREHOUSE';
    this.title = warehousingMasterTypeLabel(type);
    this.setupDescription = warehousingMasterTypeDescription(type);
    this.listRoute = routeType === 'CATEGORY' ? warehousingMasterTypeRoute('CATEGORY') : warehousingMasterTypeRoute(type);
    this.breadcrumbItems = this.isStandaloneContext
      ? [{ label: 'Setup' }, { label: this.title, routerLink: this.listRoute }, { label: this.isEditMode ? 'Edit' : 'New' }]
      : warehousingBreadcrumb(this.isEditMode ? 'Edit' : 'New', this.title, this.listRoute);
    this.masterForm.patchValue({ id: id || null, type, parentId: parentCategoryId });
    this.countries = this.countryOptions();
    this.configureRelationshipValidators(type);
    this.registerRelationshipResets();
    this.registerWarehouseOwnershipRules();
    this.loadRelationshipOptions();
    if (id) this.loadExisting(type, id);
  }

  loadRelationshipOptions(): void {
    forkJoin({
      clients: this.crmService.getClients(),
      suppliers: this.crmService.getClients(undefined, ['SUPPLIER', 'WAREHOUSE_SUPPLIER']),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION', { activeStatus: 'ACTIVE' }),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }),
      categories: this.warehousingService.getMasterData('CATEGORY', { activeStatus: 'ACTIVE' }),
      brands: this.warehousingService.getMasterData('BRAND', { activeStatus: 'ACTIVE' })
    }).subscribe(response => {
      this.clients = response.clients || [];
      this.suppliers = [...(response.suppliers || [])].sort((left, right) =>
        (left.companyName || '').localeCompare(right.companyName || '')
      );
      this.accounts = response.accounts || [];
      this.masterData.REGION = response.regions.data || [];
      this.masterData.WAREHOUSE = response.warehouses.data || [];
      this.warehouseOptions = companyOwnedWarehouses(this.masterData.WAREHOUSE);
      this.masterData.CATEGORY = response.categories.data || [];
      this.masterData.BRAND = response.brands.data || [];
      this.ensureAllowedParentWarehouseSelection();
    });
  }

  loadExisting(type: WarehousingMasterType, id: number): void {
    this.isLoading = true;
    this.warehousingService.getMasterData(type).subscribe({
      next: resp => {
        const item = (resp.data || []).find(row => row.id === id);
        if (item) {
          const { regionMappings, ...baseItem } = item as WarehousingMasterData;
          this.masterForm.patchValue({
            ...(baseItem as any),
            categoryId: type === 'BRAND' ? item.categoryId || item.parentId || null : item.categoryId || null
          });
          this.ensureAllowedParentWarehouseSelection();
          if (type === 'REGION') {
            this.setRegionMappings(regionMappings || []);
          }
        }
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  saveMasterData(): void {
    if (this.isSaving) {
      return;
    }
    if (this.masterForm.invalid) {
      this.masterForm.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.warehousingService.saveMasterData(this.masterDataPayload()).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: resp => {
        if (!resp.success) return;
        this.messageService.add({ severity: 'success', summary: 'Setup Saved', detail: 'Warehousing setup value was saved.' });
        if (this.isDialogMode && this.dialogRef) {
          this.dialogRef.close(resp.data || this.masterDataPayload());
          return;
        }
        if (this.isEmbeddedMode) {
          this.postEmbeddedMessage(this.returnEvent, resp.data || this.masterDataPayload());
          return;
        }
        this.router.navigateByUrl(`${this.listRoute}/view/${resp.data?.id || this.masterForm.value.id}`);
      },
      error: err => this.messageService.add({ severity: 'error', summary: 'Warehouse not saved',
        detail: err?.error?.message || err?.error?.detail || 'Check the warehouse name and linked location city.' })
    });
  }

  discard(): void {
    if (this.isDialogMode && this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    if (this.isEmbeddedMode) {
      this.postEmbeddedMessage('WAREHOUSING_EMBEDDED_CLOSE', { returnEvent: this.returnEvent });
      return;
    }
    this.router.navigateByUrl(this.listRoute);
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  openLocationDialog(): void {
    this.quickCreateVisible = true;
  }

  onQuickCreateCreated(result: PartyQuickCreateResult): void {
    if (result.type !== 'location') return;
    const location = result.record as Location;
    if (!location?.id) return;
    this.upsertLocationOption(location);
    this.masterForm.patchValue({ locationId: location.id });
    this.masterForm.get('locationId')?.markAsDirty();
    this.masterForm.get('locationId')?.markAsTouched();
    this.quickCreateVisible = false;
  }

  quickCreateFailed(message: string): void {
    if (!message) return;
    this.messageService.add({ severity: 'error', summary: 'Location create failed', detail: message });
  }

  currentCompany(): Company | null {
    return this.sessionStorageService.getCompany() || null;
  }

  countryOptions(): any[] {
    return this.sessionStorageService.getCountryList() || [];
  }

  get regionMappings(): FormArray {
    return this.masterForm.get('regionMappings') as FormArray;
  }

  get regionMappingCount(): number {
    return this.regionMappings.length;
  }

  get uniqueRegionCountryCount(): number {
    return new Set(
      this.regionMappings.controls
        .map(control => control.get('countryId')?.value)
        .filter((value): value is number => !!value)
    ).size;
  }

  addRegionMapping(mapping?: Partial<WarehousingRegionMapping>): void {
    const group = this.fb.group({
      countryId: [mapping?.countryId ?? null, Validators.required],
      cityId: [mapping?.cityId ?? null, Validators.required]
    });
    this.regionMappings.push(group);
    if (mapping?.countryId) {
      this.loadCitiesForCountry(mapping.countryId, mapping.cityId ?? null);
    }
  }

  removeRegionMapping(index: number): void {
    this.regionMappings.removeAt(index);
    this.regionMappings.markAsDirty();
    this.regionMappings.markAsTouched();
  }

  onRegionMappingCountryChange(index: number): void {
    const group = this.regionMappings.at(index);
    const countryId = group.get('countryId')?.value as number | null;
    group.patchValue({ cityId: null }, { emitEvent: false });
    if (countryId) {
      this.loadCitiesForCountry(countryId);
    }
    group.get('cityId')?.markAsTouched();
  }

  cityOptionsFor(index: number): City[] {
    const countryId = this.regionMappings.at(index)?.get('countryId')?.value as number | null;
    return countryId ? this.citiesByCountryId.get(countryId) || [] : [];
  }

  regionMappingCountryName(index: number): string {
    const countryId = this.regionMappings.at(index)?.get('countryId')?.value as number | null;
    return this.countries.find(country => country.id === countryId)?.name || 'Select country';
  }

  regionMappingCityName(index: number): string {
    const cityId = this.regionMappings.at(index)?.get('cityId')?.value as number | null;
    return this.cityOptionsFor(index).find(city => city.id === cityId)?.cityName || 'Select city';
  }

  defaultCountryId(): number | null {
    return this.currentCompany()?.countryId || null;
  }

  categoryOptions(): WarehousingMasterData[] {
    const clientId = this.masterForm.value.crmClientId;
    const accountId = this.masterForm.value.crmAccountId;
    const currentId = this.masterForm.value.id;
    const sourceRows = this.masterData.CATEGORY.filter(row =>
      row.id !== currentId &&
      (!row.crmClientId || !clientId || row.crmClientId === clientId) &&
      (!row.crmAccountId || !accountId || row.crmAccountId === accountId)
    );
    return this.categoryTreeOptions(sourceRows);
  }

  brandOptions(): WarehousingMasterData[] {
    const categoryId = this.masterForm.value.categoryId;
    return this.masterData.BRAND.filter(row => !categoryId || row.parentId === categoryId || row.categoryId === categoryId);
  }

  relationshipRequired(controlName: string): boolean {
    return this.masterForm.get(controlName)?.hasValidator(Validators.required) || false;
  }

  isThirdPartyVirtualWarehouse(): boolean {
    return `${this.masterForm.get('warehouseOwnershipType')?.value || 'COMPANY'}`.toUpperCase() === 'THIRD_PARTY_VIRTUAL';
  }

  supplierLabel(): string {
    const ownership = `${this.masterForm.get('warehouseOwnershipType')?.value || 'COMPANY'}`.toUpperCase();
    return ownership.startsWith('THIRD_PARTY_') ? 'Cold-store Custodian' : 'Supplier Optional';
  }

  warehouseOptionSubtitle(row: WarehousingMasterData | null | undefined): string {
    if (!row) {
      return 'Company-owned warehouse';
    }
    return [row.code, row.parentName, row.locationName].filter(Boolean).join(' | ') || 'Company-owned warehouse';
  }

  get nameLabel(): string {
    return this.type === 'TENANT_UOM' ? 'UOM Name' : 'Name';
  }

  get codeLabel(): string {
    return this.isWarehouse ? 'Warehouse Code (generated)' : this.type === 'TENANT_UOM' ? 'UOM Code Optional' : 'Code Optional';
  }

  private configureRelationshipValidators(type: WarehousingMasterType): void {
    const controls = ['parentId', 'categoryId', 'brandId', 'locationId'] as const;
    controls.forEach(control => {
      this.masterForm.get(control)?.clearValidators();
      this.masterForm.get(control)?.updateValueAndValidity({ emitEvent: false });
    });

    if (type === 'WAREHOUSE') {
      this.masterForm.get('parentId')?.setValidators(Validators.required);
      this.masterForm.get('locationId')?.setValidators(Validators.required);
    }
    if (type === 'BRAND') this.masterForm.get('categoryId')?.setValidators(Validators.required);
    if (type === 'SUB_BRAND') this.masterForm.get('brandId')?.setValidators(Validators.required);
    controls.forEach(control => this.masterForm.get(control)?.updateValueAndValidity({ emitEvent: false }));
  }

  private upsertLocationOption(location: Location): void {
    const index = this.locations.findIndex(option => option.id === location.id);
    if (index >= 0) {
      this.locations = this.locations.map(option => option.id === location.id ? { ...option, ...location } : option);
      return;
    }
    this.locations = [...this.locations, location].sort((left, right) => (left.name || '').localeCompare(right.name || ''));
  }

  private registerRelationshipResets(): void {
    this.masterForm.get('crmClientId')?.valueChanges.subscribe(clientId => {
      const accountId = this.masterForm.value.crmAccountId;
      if (accountId && !this.filteredAccounts(clientId).some(account => account.id === accountId)) {
        this.masterForm.patchValue({ crmAccountId: null }, { emitEvent: false });
      }
    });
  }

  private registerWarehouseOwnershipRules(): void {
    if (!this.isWarehouse) {
      return;
    }
    this.masterForm.get('warehouseOwnershipType')?.valueChanges.subscribe(value =>
      this.applyWarehouseOwnershipRules(`${value || 'COMPANY'}`)
    );
    this.applyWarehouseOwnershipRules(`${this.masterForm.get('warehouseOwnershipType')?.value || 'COMPANY'}`);
  }

  private applyWarehouseOwnershipRules(ownershipType: string): void {
    const supplierControl = this.masterForm.get('supplierClientId');
    const isThirdParty = `${ownershipType || 'COMPANY'}`.toUpperCase().startsWith('THIRD_PARTY_');

    if (isThirdParty) {
      supplierControl?.setValidators(Validators.required);
    } else {
      supplierControl?.clearValidators();
    }

    supplierControl?.updateValueAndValidity({ emitEvent: false });
  }

  private ensureAllowedParentWarehouseSelection(): void {
    if (this.type !== 'ZONE') {
      return;
    }
    const parentId = this.masterForm.get('parentId')?.value;
    if (!parentId) {
      return;
    }
    if (!this.warehouseOptions.some(row => row.id === parentId)) {
      this.masterForm.patchValue({ parentId: null }, { emitEvent: false });
    }
  }

  private masterDataPayload(): WarehousingMasterData {
    const payload = this.masterForm.getRawValue() as WarehousingMasterData;
    if (payload.type !== 'WAREHOUSE') {
      delete payload.supplierClientId;
      delete payload.supplierClientName;
      delete payload.supplierWarehouseName;
      delete payload.warehouseOwnershipType;
    }
    if (payload.type === 'BRAND') {
      payload.parentId = payload.categoryId || payload.parentId;
    }
    if (payload.type !== 'REGION') {
      delete payload.regionMappings;
      delete payload.defaultWarehouseId;
      delete payload.defaultWarehouseName;
    } else {
      payload.regionMappings = (payload.regionMappings || []).map(mapping => ({
        countryId: Number(mapping.countryId),
        cityId: Number(mapping.cityId)
      }));
    }
    return payload;
  }

  private setRegionMappings(mappings: WarehousingRegionMapping[]): void {
    this.regionMappings.clear();
    mappings.forEach(mapping => this.addRegionMapping(mapping));
  }

  private loadCitiesForCountry(countryId: number, preferredCityId?: number | null): void {
    if (this.citiesByCountryId.has(countryId)) {
      return;
    }
    this.baseMasterDataService.getCities('en-US', Number(countryId)).subscribe(cities => {
      this.citiesByCountryId.set(countryId, cities ?? []);
      if (preferredCityId) {
        const row = this.regionMappings.controls.find(control => control.get('countryId')?.value === countryId
          && !control.get('cityId')?.value);
        if (row && (cities ?? []).some(city => city.id === preferredCityId)) {
          row.patchValue({ cityId: preferredCityId }, { emitEvent: false });
        }
      }
    });
  }

  private categoryTreeOptions(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    const byParent = new Map<number, WarehousingMasterData[]>();
    const ids = new Set(rows.map(row => row.id).filter((id): id is number => !!id));
    rows.forEach(row => {
      if (!row.parentId) return;
      const children = byParent.get(row.parentId) || [];
      children.push(row);
      byParent.set(row.parentId, children);
    });
    const output: WarehousingMasterData[] = [];
    const append = (row: WarehousingMasterData, depth: number): void => {
      output.push({ ...row, name: `${'-- '.repeat(depth)}${row.name}` });
      (row.id ? byParent.get(row.id) || [] : [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach(child => append(child, depth + 1));
    };
    rows
      .filter(row => !row.parentId || !ids.has(row.parentId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(row => append(row, 0));
    return output;
  }

  private postEmbeddedMessage(type: string, payload: unknown): void {
    window.parent?.postMessage({ type, payload }, window.location.origin);
  }
}
